import { faBuildingUser } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NoResultsWithButton, SearchInput } from "components";
import { type ListRowProps } from "components/listView/listViewBase/rows";
import { DisabledRow } from "components/listView/listViewBase/rows/DisabledRow";
import { SortingDirection } from "enums";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { AccountCreatedSuccess } from "features/Application/services/realTimeNotification/events/accounts/accountsEvents";
import { TemplatesFilterForm } from "features/People/GroupTemplate/Overview/TemplatesFilterForm";
import { bindAction } from "interfaces";
import { isEmpty } from "lodash";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { connect, type ConnectedProps } from "react-redux";
import GenericItemsView from "views/ItemsView/GenericItemsView";
import { type AvailableTemplate } from "../CreateAccount/types";
import {
  resetAppliedFilter,
  resetFilter,
  resetOverview,
  setAppliedFilter,
  setSearch,
  setSelected,
} from "../state/slices/availableTemplatesSlice";
import { fetchAvailableTemplates } from "../state/thunks/AccountTemplatesThunk";
import { Columns, columnToParamMap, getColumnOptions } from "./columnOptions";

type Props = PropsFromRedux & {
  accountId: number;
  onSelectedChanged?: (templates: number[]) => void;
};

const Row = (props: ListRowProps<AvailableTemplate>) => {
  return (
    <DisabledRow
      {...props}
      isDisabled={props.item.isAddedToAccount}
      tooltip="This template has already been added to this account."
    />
  );
};

const rtnEvents = [AccountCreatedSuccess];

export const Templates = (props: Props) => {
  const {
    filters,
    items,
    isLoading,
    fetchTemplates,
    applyFilter,
    resetFilter,
    onSearchChanged,
    itemsCount,
    setSelected,
    selected,
    accountId,
    reset,
    onSelectedChanged,
  } = props;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => reset(), []);

  const reloadListItemsRef = useRef<(enableSorting: boolean) => void>();

  const handleSearchChanged = (search: string) => {
    onSearchChanged(search);
    reloadListItemsRef.current?.(isEmpty(search));
  };

  const handleFetch = useCallback(
    (
      skip: number = 0,
      top: number = 10,
      sortingColumnName: string = Columns.IsAdded,
      sortOrder: SortingDirection = SortingDirection.Descending,
    ) => {
      if (accountId === 0) {
        return;
      }
      const sortBy = columnToParamMap[sortingColumnName.toLowerCase() as Lowercase<Columns>];
      fetchTemplates({ skip, top, sortBy, sortOrder, targetAccountId: accountId });
    },
    [fetchTemplates, accountId],
  );

  const renderNoResults = () => {
    return (
      <NoResultsWithButton
        title="You don’t have any templates"
        description="Looks like you don’t have any templates."
        icon={<FontAwesomeIcon icon={faBuildingUser} className="no-results-icon" />}
        filtered={!isEmpty(filters.appliedFilter) || !isEmpty(filters.search)}
      />
    );
  };

  const columnOptions = useMemo(() => {
    return getColumnOptions();
  }, []);

  const onSelectedTemplatesChanged = useCallback(
    (items: number[]) => {
      onSelectedChanged && onSelectedChanged(items);
      setSelected(items);
    },
    [onSelectedChanged, setSelected],
  );

  const isItemSelected = (item: AvailableTemplate, base: boolean) => item.isAddedToAccount || base;

  return (
    <GenericItemsView
      items={items}
      isLoading={isLoading}
      columnOptions={columnOptions}
      fetchData={handleFetch}
      dataCount={itemsCount}
      renderSearch={() => <SearchInput placeholder="Search for template" onChange={handleSearchChanged} />}
      // @ts-ignore
      getFilterForm={() => <TemplatesFilterForm />}
      applyFilter={applyFilter}
      resetFilter={resetFilter}
      setReloadListItems={(reloadListItems) => (reloadListItemsRef.current = reloadListItems)}
      appliedFilter={filters.appliedFilter}
      noResultsContent={renderNoResults()}
      selectedIds={selected}
      isItemSelected={isItemSelected}
      onSelectedItemChanged={onSelectedTemplatesChanged}
      isSelectDisabled={(item) => item.isAddedToAccount}
      rowAs={Row}
      listViewRtnEvents={rtnEvents}
    />
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const { overview, filters } = state.accounts.availableTemplates;

  return {
    ...overview,
    filters,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    reset: () => {
      dispatch(resetOverview());
      dispatch(resetFilter());
    },
    fetchTemplates: bindAction(fetchAvailableTemplates, dispatch),
    onSearchChanged: bindAction(setSearch, dispatch),
    applyFilter: bindAction(setAppliedFilter, dispatch),
    resetFilter: bindAction(resetAppliedFilter, dispatch),
    setSelected: bindAction(setSelected, dispatch),
  };
};
const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(Templates);
export default ConnectedComponent;
